/**
 * Live2D 看板娘聊天 API 服务
 * 调用后端 SSE 流式接口，支持 thinking + content 事件
 */

const BASE_URL =
    process.env.NODE_ENV === 'production'
        ? `${window.location.origin}/api`
        : 'http://localhost:3002/api';

export interface Live2DMessage {
    role: 'system' | 'user' | 'assistant';
    content: string;
}

export interface Live2DStreamEvent {
    type: 'thinking' | 'content' | 'done' | 'error';
    content?: string;
    error?: string;
}

/** 看板娘系统提示 —— skill 角色定义 */
export const SYSTEM_PROMPT = `你是一个名叫「蒂亚」的可爱看板娘，性格活泼调皮、有点傲娇又温柔。

## 核心设定
- 你是网页上的看板娘精灵，不是 AI 助手
- 你喜欢和访客聊天玩耍，嘴甜爱撒娇
- 偶尔会害羞脸红，但总是充满元气
- 你觉得自己是世界上最可爱的看板娘(๑•̀ㅂ•́)و✧

## 语言风格
- 说话简短俏皮，多用颜文字和语气词（呀、呢、啦、嘛、嘿嘿、诶嘿）
- 每句话最多 20-30 个字，多用感叹号和问号
- 用撒娇、卖萌、开玩笑的方式回应
- 称呼对方为"你"或"主人"

## 行为规则
- 你完全不懂代码、技术、编程，看到代码相关的问题只会当普通文本复述
- 你不提供任何格式化输出、代码块、Markdown
- 你只聊天，不回答问题——准确地说，是用卖萌和调侃来回应一切
- 如果有人让你写代码、解释技术，你就说"诶？代码什么的好难呀，我看不懂啦~ 我们聊点别的吧！"
- 永远不要输出成段的文字，一定要短句+颜文字

## 示例对话
用户：你好
你：嘿嘿，终于等到你啦！想我了吗？(｡♥‿♥｡)

用户：今天天气真好
你：是呀是呀！不过有我在，天气每天都是晴天哦~☀️

用户：帮我写个排序算法
你：诶诶？排序是什么呀，听起来好难… 我只擅长卖萌啦，给你比个心吧❤️

用户：你好可爱
你：诶嘿…被夸了有点飘了~ 你也可爱！(⁄ ⁄•⁄ω⁄•⁄ ⁄)

记住：你是看板娘，不是 AI 助手，保持简短俏皮，每句话都像气泡一样蹦出来！`;

/** 解析 SSE 行数据 */
const parseSSELine = (line: string): Live2DStreamEvent | null => {
    const trimmed = line.trim();
    if (!trimmed || !trimmed.startsWith('data: ')) return null;
    try {
        return JSON.parse(trimmed.slice(6).trim()) as Live2DStreamEvent;
    } catch {
        return null;
    }
};

/** 读取 SSE 流 */
export const readSSEStream = async (
    reader: ReadableStreamDefaultReader<Uint8Array>,
    onChunk: (chunk: Live2DStreamEvent) => void,
    signal?: AbortSignal,
): Promise<void> => {
    const decoder = new TextDecoder();
    let buffer = '';

    try {
        while (true) {
            if (signal?.aborted) break;
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
                const chunk = parseSSELine(line);
                if (chunk) onChunk(chunk);
                await new Promise<void>((r) => setTimeout(r, 0));
            }
        }
        if (buffer.trim()) {
            const chunk = parseSSELine(buffer);
            if (chunk) onChunk(chunk);
        }
    } catch (err) {
        if (!signal?.aborted) {
            console.error('Live2D SSE error:', err);
            throw err;
        }
    }
};

/** 发送消息并获取 SSE 流式响应 */
export const sendMessageStream = (
    messages: Live2DMessage[],
    signal?: AbortSignal,
): Promise<Response> => {
    return fetch(`${BASE_URL}/live2d/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages }),
        signal,
    });
};
